import { FC } from "react";
import { Trans, useTranslation } from "react-i18next";
import OutsideClickHandler from "react-outside-click-handler";
import { BriefcaseIcon, CloseIconThin, PinFilledIcon, PinIcon } from "assets/svg";

import { Button, Text } from "uikit";

import { useCompanySwitcherDropdown } from "./useCompanySwitcherDropdown";
import {
  AddNewCompanyButtonIcon,
  ArrowIcon,
  ClosedContainerInner,
  ClosedDropdownMainContent,
  CloseIconContainer,
  CompanyListItem,
  CompanyListItemSideContent,
  Container,
  DropdownContent,
  EmptyStateContainer,
  IconContainer,
  InvitedLabel,
  List,
  ListContainer,
  PinIconContainer,
  StyledFullSectionLoader,
  StyledSearchInput,
} from "./styles";

export const CompanySwitcherDropdown: FC = () => {
  const { t } = useTranslation();
  const translationPrefix = "components.company_switcher_dropdown";
  const { metadata, actions, data } = useCompanySwitcherDropdown();

  const { isOpen, isListLoading, currentCompany } = metadata;
  const {
    handleDropdownClick,
    handleCreateNewCompanyClick,
    handleCompanyClick,
    handleSearchInputChange,
    handlePinCompanyClick,
  } = actions;
  const { companiesList, searchInputValue } = data;
  const shouldShowCreateNewCompanyButton = !isListLoading;
  const shouldShowEmptyState = !companiesList.total && !isListLoading;
  const companyId = currentCompany?.companyId;

  const renderEmptyState = () => {
    return (
      <EmptyStateContainer>
        <Text $color="gray.500_main" $fs="t12">
          <Trans i18nKey={`${translationPrefix}.no_results_found`} />
        </Text>
      </EmptyStateContainer>
    );
  };

  const renderPinIcon = (companyId: string, isPinned: boolean) => {
    const component = isPinned ? <PinFilledIcon /> : <PinIcon />;
    return (
      <PinIconContainer
        onClick={(e) => {
          e.stopPropagation();
          handlePinCompanyClick(companyId, isPinned);
        }}
        data-testid={`pin-company-icon-${companyId}`}
      >
        {component}
      </PinIconContainer>
    );
  };

  return (
    <OutsideClickHandler onOutsideClick={() => handleDropdownClick(false)}>
      <Container
        isOpen={isOpen}
        onClick={() => (!isOpen ? handleDropdownClick(true) : {})}
        data-testid="company-switcher-dropdown"
      >
        <ClosedContainerInner isOpen={isOpen}>
          <ClosedDropdownMainContent>
            <IconContainer>
              <BriefcaseIcon />
            </IconContainer>

            <Text component="span" $fs="t14" $fw="semiBold">
              {currentCompany?.name}
            </Text>
          </ClosedDropdownMainContent>

          {isOpen ? (
            <CloseIconContainer
              onClick={() => handleDropdownClick(false)}
              data-testid="close-company-switcher-dropdown-button"
            >
              <CloseIconThin />
            </CloseIconContainer>
          ) : (
            <ArrowIcon />
          )}
        </ClosedContainerInner>

        <DropdownContent isOpen={isOpen}>
          <StyledSearchInput
            value={searchInputValue}
            onChange={handleSearchInputChange}
            placeholder={t(`common.input.search_placeholder`)}
          />
          <Text $color="gray.500_main" $fs="t12" component="span">
            <Trans
              i18nKey={`${translationPrefix}.showing_companies_count`}
              values={{ count: companiesList.total }}
            />
          </Text>

          <ListContainer>
            {shouldShowEmptyState ? (
              renderEmptyState()
            ) : (
              <List hideListDecoration isLoading={isListLoading}>
                {isListLoading && <StyledFullSectionLoader />}
                {companiesList.companies.map((company) => {
                  const isSelected = company.companyId === companyId;
                  const showInvitedLabel = !company.hasSignedIn;
                  const showPinIcon = !showInvitedLabel;
                  return (
                    <CompanyListItem
                      key={company.companyId}
                      isSelected={isSelected}
                      onClick={() => (!isSelected ? handleCompanyClick(company.companyId) : {})}
                      data-testid={`company-switcher-dropdown-item-${company.companyId}`}
                    >
                      <Text component="span" $fs="t14" $fw="medium" $noWrap>
                        {company.name}
                      </Text>

                      <CompanyListItemSideContent>
                        {!company.hasSignedIn && (
                          <InvitedLabel>
                            <Trans i18nKey={`${translationPrefix}.invited_label`} />
                          </InvitedLabel>
                        )}
                        {isSelected && (
                          <Text component="span" $fs="t12" $fw="medium">
                            <Trans i18nKey={`${translationPrefix}.selected_label`} />
                          </Text>
                        )}

                        {showPinIcon && renderPinIcon(company.companyId, !!company.pinnedAt)}
                      </CompanyListItemSideContent>
                    </CompanyListItem>
                  );
                })}
              </List>
            )}
            {shouldShowCreateNewCompanyButton && (
              <Button
                $variant="secondary"
                $size="small"
                onClick={handleCreateNewCompanyClick}
                data-testid="create-new-company-button"
                $m={"8px 0"}
              >
                <AddNewCompanyButtonIcon />
                <Trans i18nKey={`${translationPrefix}.create_new_company_button`} />
              </Button>
            )}
          </ListContainer>
        </DropdownContent>
      </Container>
    </OutsideClickHandler>
  );
};
